import React, { useId } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Button } from '@els/els-react--button';
import { Icon } from '@els/els-react--icon';

import './SummaryTrailingActions.scss';

/**
 * Shared Assistant/Filters bottom section.
 *
 * The surrounding container owns the state decision and supplies copy/content plus actions. This
 * component only owns the common Figma structure: divider, optional AI avatar, response content,
 * and an icon-free CTA row.
 */
function SummaryTrailingActions({
  actions,
  ariaLive,
  body,
  children,
  className,
  heading,
  role,
  selectionError,
  showIcon = true,
  sticky = true,
  testId = 'summary-trailing-actions',
  variant,
}) {
  const hasCopy = heading || body;
  const hasSelectionAction = actions.some(({ isSelectionAction }) => isSelectionAction);
  const selectionErrorId = useId();

  return (
    <div
      className={classnames(
        'ai-search-summary__trailing-actions',
        {
          'ai-search-summary__trailing-actions--actions-only': variant === 'actions-only',
          'ai-search-summary__trailing-actions--static': !sticky,
        },
        className
      )}
      role={role}
      aria-live={ariaLive}
      data-testid={testId}>
      <div className="ai-search-summary__trailing-response">
        {showIcon && (
          <span className="ai-search-summary__trailing-avatar" aria-hidden="true">
            <span className="ai-search-summary__trailing-avatar-inner">
              <Icon sprite="AI" size="xxs" isDecorative className="ai-search-summary__trailing-avatar-icon" />
            </span>
          </span>
        )}
        <div className="ai-search-summary__trailing-body">
          {hasCopy && (
            <div className="ai-search-summary__trailing-copy">
              {heading && (
                <p>
                  <strong>{heading}</strong>
                </p>
              )}
              {body && <p>{body}</p>}
            </div>
          )}
          {children}
          <div className="ai-search-summary__trailing-buttons">
            {actions.map(
              ({
                buttonRef,
                className: actionClassName,
                dataE2e,
                isSelectionAction,
                label,
                onClick,
                size = 'small',
                type = 'secondary',
              }) => (
                <Button
                  ref={buttonRef}
                  key={dataE2e || label}
                  type={type}
                  size={size}
                  className={actionClassName}
                  aria-describedby={isSelectionAction && selectionError ? selectionErrorId : undefined}
                  onClick={onClick}
                  data-e2e={dataE2e}>
                  {label}
                </Button>
              )
            )}
          </div>
          {hasSelectionAction && (
            <div
              id={selectionErrorId}
              role="alert"
              className={selectionError ? 'ai-search-summary__selection-error u-els-color-n-error' : undefined}>
              {selectionError && (
                <>
                  <span className="u-els-hide-visually">Error: </span>
                  {selectionError}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

SummaryTrailingActions.propTypes = {
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      buttonRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
      className: PropTypes.string,
      dataE2e: PropTypes.string,
      isSelectionAction: PropTypes.bool,
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      size: PropTypes.oneOf(['default', 'x-small', 'small', 'large']),
      type: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'info', 'warn', 'confirm', 'link', 'transparent', 'borderless']),
    })
  ).isRequired,
  ariaLive: PropTypes.oneOf(['off', 'polite', 'assertive']),
  body: PropTypes.node,
  children: PropTypes.node,
  className: PropTypes.string,
  heading: PropTypes.node,
  role: PropTypes.string,
  selectionError: PropTypes.string,
  showIcon: PropTypes.bool,
  sticky: PropTypes.bool,
  testId: PropTypes.string,
  variant: PropTypes.oneOf(['actions-only']),
};

export default SummaryTrailingActions;
